import { useEffect, useState } from 'react';
import { useCurrentUserId } from '@framework/auth';
import { useParams } from 'react-router';
import { TextRepository } from '@framework/repository';
import { RTDBPath } from 'balus-common';
import type { RouteParams } from '@user/pages/RouteParams';

type UseIsSidebarAvailableResult = {
  available: boolean;
  /** サイドバー利用可否の非同期判定が確定するまでは true */
  loading: boolean;
};

export const useIsSidebarAvailable = (): UseIsSidebarAvailableResult => {
  const [result, setResult] = useState<UseIsSidebarAvailableResult>({ available: false, loading: true });

  const { groupId, workspaceId, viewModelId } = useParams<RouteParams>();

  const currentUserId = useCurrentUserId();

  useEffect(() => {
    const settle = (available: boolean): void => {
      setResult({ available, loading: false });
    };

    if (!currentUserId) {
      settle(false);
      return;
    }

    const isGroupRegularMember = async (groupId: string): Promise<boolean> => {
      const role = await new TextRepository(RTDBPath.Group.memberRolePath(groupId, currentUserId)).get();
      return role === 'admin' || role === 'member';
    };

    const checkGroupMember = async (groupId: string): Promise<void> => {
      settle(await isGroupRegularMember(groupId));
    };

    const checkWorkspaceMember = async (workspaceId: string): Promise<void> => {
      const workspaceRole = await new TextRepository(
        RTDBPath.Workspace.memberRolePath(workspaceId, currentUserId)
      ).get();
      const groupId = await new TextRepository(RTDBPath.Workspace.workspaceGroupIdPath(workspaceId)).get();

      settle(!!workspaceRole && !!groupId && (await isGroupRegularMember(groupId)));
    };

    const checkWorkspaceMemberWithViewModelId = async (viewModelId: string): Promise<void> => {
      const workspaceId = await new TextRepository(RTDBPath.ViewModel.viewModelWorkspaceIdPath(viewModelId)).get();

      if (!workspaceId) {
        settle(false);
        return;
      }

      await checkWorkspaceMember(workspaceId);
    };

    // グループページ
    if (groupId) {
      checkGroupMember(groupId);
      return;
    }

    // ワークスペースページ
    if (workspaceId) {
      checkWorkspaceMember(workspaceId);
      return;
    }

    // ビューモデルページ
    if (viewModelId) {
      checkWorkspaceMemberWithViewModelId(viewModelId);
      return;
    }

    settle(false);
  }, [currentUserId, groupId, workspaceId, viewModelId]);

  return result;
};
